import React, { PropsWithChildren } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, HStack, ShimmerText, Text, VStack } from '@betterment/samba-react';
import { formatNumberWithDecimals } from '@betterment/shared-components';

import { EnvelopeRowMenu } from './EnvelopeRowMenu';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdEnvelopeYtdReturnsQuery } from './graphql/query.householdEnvelopeYtdReturns.generated';
import { RowCell } from './RowCell';
import { TargetField } from './TargetField';
import { formatAccountHolders } from './utils';

type HouseholdData = Extract<HouseholdAccountsQuery['household'], { __typename: 'Household' }>;
type Envelope = HouseholdData['accounts'][number]['envelope'];
type AccountHolder = HouseholdData['accounts'][number]['accountHolders'][number];
type HouseholdEnvelopeYtd = Extract<HouseholdEnvelopeYtdReturnsQuery['household'], { __typename: 'Household' }>;
type YtdReturn = HouseholdEnvelopeYtd['envelopeYtdReturns'][number]['ytdReturn'];

function EnvelopeRowLayout({ children, showYtdReturns }: PropsWithChildren<{ showYtdReturns: boolean }>) {
  const gridColumns = showYtdReturns
    ? '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 200px) minmax(auto, 36px)'
    : '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 36px)';

  return (
    <Box
      css={{
        width: '100%',
        gridColumn: '1 / -1',
      }}
      role="row"
    >
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gridAutoRows: 'auto',
          color: 'text',
          paddingTop: 'space.300',
          paddingBottom: 'space.300',
          borderTop: 'solid',
          borderWidth: 'thin',
          borderColor: 'border.weak',
          minHeight: '112px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export const EnvelopeRow: React.FC<{
  envelope: Envelope;
  accountHolders: AccountHolder[];
  accountCount: number;
  ytdReturn?: YtdReturn;
  ytdReturnsLoading: boolean;
  showYtdReturns: boolean;
}> = ({ envelope, accountHolders, accountCount, ytdReturn, ytdReturnsLoading, showYtdReturns }) => {
  if (!envelope.purpose) {
    return null;
  }

  const iconUrl = envelope.purpose.icon.light;
  const formattedAccountHolders = formatAccountHolders(accountHolders);

  return (
    <EnvelopeRowLayout key={envelope.id} showYtdReturns={showYtdReturns}>
      <RowCell css={{ gridColumn: '1' }}>
        <Box>
          <Text>{formattedAccountHolders}</Text>
        </Box>
      </RowCell>
      <RowCell css={{ gridColumn: '2' }}>
        <HStack css={{ alignItems: 'center', gap: 'space.150' }}>
          <Box css={{ width: 'size.450', height: 'size.450' }}>
            <img src={iconUrl} alt="Icon" />
          </Box>
          <VStack>
            <Text color="text" className="goal-title">
              {envelope.purpose.name}
            </Text>
            <Text variant="text50" color="text.weak">
              {accountCount === 1 ? '1 account' : `${accountCount} accounts`}
            </Text>
          </VStack>
        </HStack>
      </RowCell>
      <RowCell css={{ gridColumn: '3' }}>
        <TargetField purpose={envelope.purpose} />
      </RowCell>
      {showYtdReturns && (
        <RowCell css={{ gridColumn: '4', justifyContent: 'end', paddingRight: '0' }}>
          {ytdReturnsLoading && <ShimmerText textStyle="text100" css={{ maxWidth: '80%' }} />}
          {!ytdReturnsLoading && ytdReturn && (
            <VStack css={{ alignItems: 'flex-end', gap: 'space.50' }}>
              <Text
                variant="text100"
                color={
                  ytdReturn.moneyWeighted !== null &&
                  ytdReturn.moneyWeighted !== undefined &&
                  ytdReturn.moneyWeighted > 0
                    ? 'text.success'
                    : 'text'
                }
              >
                {ytdReturn.moneyWeighted !== null && ytdReturn.moneyWeighted !== undefined
                  ? `${ytdReturn.moneyWeighted > 0 ? '+' : ''}${formatNumberWithDecimals(ytdReturn.moneyWeighted, 2)}%`
                  : '—'}
              </Text>
            </VStack>
          )}
          {!ytdReturnsLoading && !ytdReturn && <Text color="text.weak">-</Text>}
        </RowCell>
      )}
      <RowCell css={{ gridColumn: showYtdReturns ? '5' : '4', justifyContent: 'end', paddingRight: 'space.300' }}>
        <VStack>
          <Text>{envelope.balance ? formatMoney(envelope.balance) : ''}</Text>
          {ytdReturn?.amount !== null && ytdReturn?.amount !== undefined && (
            <Text
              variant="text50"
              color={ytdReturn.amount.cents > 0 ? 'text.success' : 'text'}
              css={{ textAlign: 'right' }}
            >
              {ytdReturn.amount.cents > 0 ? '+' : ''}
              {formatMoney(ytdReturn.amount)}
            </Text>
          )}
        </VStack>
      </RowCell>
      <RowCell>
        <EnvelopeRowMenu envelope={envelope} />
      </RowCell>
    </EnvelopeRowLayout>
  );
};
